<template>
  <model-form
    ref="modelForm"
    :model="discount"
    modal-type="modal"
    width="700px"
  >
    <q-tabs
      :model-value="tab"
      align="justify"
      dense
      @update:model-value="onTabChange"
    >
      <q-tab
        name="details"
        label="Details"
      />
      <q-tab
        name="restrictions"
        label="Restrictions"
      />
      <q-tab
        v-if="(discount.type === 'ticket' || discount.type === 'activity-ticket') && $isPermitted($permissions.OperationsReporting.View)"
        name="reporting"
        label="Reporting"
      />
    </q-tabs>
    <div class="row q-gutter-sm q-pt-md">
      <model-form-input
        field="name"
        class="col"
        label="Name"
        :rules="[val => val !== null || 'Field is required']"
        autofocus
      />
      <model-form-select
        field="type"
        class="col"
        label="Type"
        :options="discount.fieldOptions('type')"
        emit-value
        map-options
        :rules="[val => val !== null || 'Field is required']"
      />
    </div>
    <q-tab-panels v-model="tab">
      <q-tab-panel
        name="details"
        class="q-pt-none"
      >
        <div class="column">
          <div class="row q-col-gutter-sm items-center">
            <model-form-toggle
              field="hasCustomDisplayName"
              class="col-6 q-pb-md"
              label="Has Custom Display Name"
            >
              <q-tooltip>Display name is what is shown to the user during checkout flow and on receipts</q-tooltip>
            </model-form-toggle>
            <model-form-input
              v-if="discount.hasCustomDisplayName"
              field="customDisplayName"
              class="col-6"
              label="Custom Display Name"
              :rules="[val => !!val || 'Field is required']"
            />
            <model-form-input
              v-else
              field="displayName"
              readonly
              class="col-6"
              label="Display Name"
              bottom-slots
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <model-form-input
              field="description"
              class="col-12"
              label="Description (Used to describe membership benefits)"
              clearable
              :rules="[
                val => !val || val.length < 256 || 'Max length 255 characters'
              ]"
            />
          </div>
          <div class="text-h6 q-mb-sm q-mt-md">
            Redemption
          </div>
          <div class="col-4 q-mb-sm">
            <model-form-toggle
              field="appliesToAllOrders"
              class="row "
              label="Automatically Available on all Orders"
              checked-icon="warning"
              icon-color="red"
            >
              <q-tooltip>This discount is available on all orders, even if not logged in.</q-tooltip>
            </model-form-toggle>
            <span
              v-if="discount.appliesToAllOrders"
              class="row q-px-sm text-caption text-weight-light text-red"
            >
              Warning: Discount is enabled for all users
            </span>
          </div>
          <div class="row q-col-gutter-sm items-center">
            <model-form-toggle
              field="availableOnline"
              class="col-6 q-pb-md"
              :label="discount.appliesToAllOrders ? 'Available Online' : 'Available Online By Code'"
              bottom-slots
            />
            <model-form-input
              v-if="!discount.appliesToAllOrders"
              field="onlineRedemptionCode"
              :disable="!discount.availableOnline"
              class="col-6"
              label="Online Redemption Code"
              :rules="[val => !discount.availableOnline || !!val || 'Field is required']"
            />
          </div>
          <div
            v-if="discount.availableOnline && !discount.appliesToAllOrders"
            class="row q-col-gutter-sm items-center"
          >
            <model-form-toggle
              field="limitRedemptions"
              class="col-6 q-pb-md"
              label="Limited Run Discount"
            >
              <q-tooltip>
                Limit Discount to a certain number of redeemptions before it is no longer available
                <br>
                E.g. 10 people can redeem this discount, once the discount has been used by 10 people it is no longer available
              </q-tooltip>
            </model-form-toggle>
            <model-form-input
              field="redemptionsLimit"
              :disable="!discount.limitRedemptions"
              class="col-6"
              min="1"
              step="1"
              label="Limited Run Limit"
              :rules="[val => (!discount.limitRedemptions || !!val) || 'Field is required']"
            />
          </div>
          <div class="row q-col-gutter-sm items-center">
            <model-form-toggle
              field="limitRedemptionsByShowingEnabled"
              class="col-4 q-pb-md"
              label="Limit Redemptions Per Showing Per User"
            >
              <q-tooltip>Limit the number of times this discount can be redeemed for a single showing by a user</q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="limitRedemptionsByShowingIncludesOverlapsEnabled"
              :disable="!discount.limitRedemptionsByShowingEnabled"
              class="col-4 q-pb-md"
              label="Limit Overlapping Showings"
            >
              <q-tooltip>Consider showings that overlap by 30 minutes or more to be the same showing when limiting redemptions</q-tooltip>
            </model-form-toggle>
            <model-form-input
              field="showingRedemptionsLimit"
              :disable="!discount.limitRedemptionsByShowingEnabled"
              class="col-4"
              label="Redemptions Per Showing"
              type="number"
              step="1"
              :rules="[val => !!val || 'Field is required']"
            />
          </div>
          <div class="row q-col-gutter-sm items-center">
            <model-form-toggle
              field="limitRedemptionsByActivitySessionEnabled"
              class="col-4 q-pb-md"
              label="Limit Redemptions Per Activity Session"
            >
              <q-tooltip>Limit the number of times this discount can be redeemed for a single activity session</q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="limitRedemptionsByActivitySessionAdmissionTimes"
              :disable="!discount.limitRedemptionsByActivitySessionEnabled"
              class="col-4 q-pb-md"
              label="Limit By Admission Times"
            >
              <q-tooltip>Consider activity session admissions with non-overlapping entry and exit times to be different activity sessions</q-tooltip>
            </model-form-toggle>
            <model-form-input
              field="activitySessionRedemptionsLimit"
              :disable="!discount.limitRedemptionsByActivitySessionEnabled"
              class="col-4"
              label="Redemptions Per Activity Session"
              type="number"
              step="1"
              :rules="[val => !!val || 'Field is required']"
            />
          </div>
          <div class="row q-col-gutter-sm items-center">
            <model-form-toggle
              field="limitRedemptionsByOrderEnabled"
              class="col-4 q-pb-md"
              label="Limit Redemptions Per Order"
            >
              <q-tooltip>Limit the number of times this discount can be redeemed for a single order</q-tooltip>
            </model-form-toggle>
            <q-space />
            <model-form-input
              field="orderRedemptionsLimit"
              :disable="!discount.limitRedemptionsByOrderEnabled"
              class="col-4"
              label="Redemptions Per Order"
              type="number"
              step="1"
              :rules="[val => !!val || 'Field is required']"
            />
          </div>

          <div class="text-h6 q-mb-sm q-mt-md">
            Value
          </div>
          <div class="row q-gutter-sm items-start">
            <model-form-select
              field="amountType"
              class="col"
              label="Type"
              :options="$mh.asOptions(DISCOUNT_AMOUNT_TYPES.ALL)"
              emit-value
              map-options
              :rules="[val => val !== null || 'Field is required']"
            />
            <model-form-input
              v-if="discount.amountType === DISCOUNT_AMOUNT_TYPES.PERCENT"
              field="percentThousandths"
              :decode="val => val / 1000"
              :encode="val => val * 1000"
              class="col"
              label="Percent"
              suffix="%"
              type="number"
              step="0.01"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
              @update:model-value="value => discount.percentThousandths = value * 1000"
            />
            <model-form-input
              v-if="discount.amountType === DISCOUNT_AMOUNT_TYPES.FIXED_AMOUNT || discount.amountType === DISCOUNT_AMOUNT_TYPES.TARGET_AMOUNT"
              field="amount"
              class="col"
              :label="discountAmountLabel"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              step="0.25"
              min="0"
              :rules="[val => val !== null && val >= 0 || 'Positive value is required']"
            />
          </div>
        </div>
      </q-tab-panel>
      <q-tab-panel
        name="restrictions"
        class="q-pt-none"
      >
        <div class="column">
          <div
            class="row q-col-sm items-start"
          >
            <discount-preview :discount="discount" />
          </div>
          <div class="text-h6 q-mb-sm q-mt-md">
            Restrictions
          </div>
          <div class="q-py-sm">
            <q-icon name="info" /> Restrictions are applied sequentially. Specifics override general, e.g. a showing will override a movie
          </div>
          <div
            v-if="discount.type === 'ticket' || discount.type === 'activity-ticket'"
            class="row q-gutter-sm items-start"
          >
            <q-select
              v-model="discount.siteTicketTypeIds"
              class="col"
              :label="'Ticket Types To ' + (discount.excludeTicketTypes ? 'Exclude' : 'Include')"
              stack-label
              :options="$_.unionBy(discount.ticketTypes.data, $_.filter(ticketTypes.data, 'isUnarchived'), 'id')"
              map-options
              emit-value
              option-value="id"
              menu-anchor="bottom left"
              outlined
              multiple
              clearable
              bottom-slots
              @clear="discount.siteTicketTypeIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.name }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="discount.excludeTicketTypes"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'ticket'"
            class="row q-gutter-sm items-start"
          >
            <q-select
              v-model="discount.titleClassIds"
              class="col"
              :label="'Title Classes To ' + (discount.excludeTitleClasses ? 'Exclude' : 'Include')"
              stack-label
              :options="$_.unionBy(discount.titleClasses.data, titleClasses.data, 'id')"
              map-options
              emit-value
              option-value="id"
              outlined
              multiple
              clearable
              bottom-slots
              @clear="discount.titleClassIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.name }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="discount.excludeTitleClasses"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'ticket'"
            class="row q-gutter-sm items-start"
          >
            <q-select
              v-model="discount.siteMovieIds"
              :label="'Movies To ' + (discount.excludeMovies ? 'Exclude' : 'Include')"
              stack-label
              class="col"
              :options="$_.unionBy(discount.siteMovies.data, movies.data, 'id')"
              map-options
              emit-value
              option-value="id"
              use-input
              input-style="search"
              input-debounce="100"
              outlined
              multiple
              clearable
              bottom-slots
              @filter="filterMovies"
              @clear="discount.siteMovieIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.name }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeMovies"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'activity-ticket'"
            class="row q-gutter-sm items-start"
          >
            <q-select
              v-model="discount.siteActivityIds"
              class="col"
              :label="'Activities To ' + (discount.excludeActivities ? 'Exclude' : 'Include')"
              stack-label
              :options="$_.unionBy(discount.siteActivities.data, activities.data, 'id')"
              map-options
              emit-value
              option-value="id"
              use-input
              input-style="search"
              input-debounce="100"
              outlined
              multiple
              clearable
              bottom-slots
              @filter="filterActivities"
              @clear="discount.siteActivityIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.name }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeActivities"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'ticket' || discount.type === 'activity-ticket'"
            class="row q-gutter-sm items-start"
          >
            <model-form-input
              field="showingRangeStart"
              class="col"
              label="Start Date"
              clearable
              :rules="[
                val => !discount.showingRangeEnd || discount.showingRangeStart <= discount.showingRangeEnd || 'Date cannot postdate end date',
              ]"
            />
            <model-form-input
              field="showingRangeEnd"
              class="col"
              label="End Date"
              clearable
              :rules="[
                val => !discount.showingRangeStart || !discount.showingRangeEnd || discount.showingRangeEnd >= discount.showingRangeStart || 'Date cannot predate start date',
              ]"
            />
          </div>
          <div
            v-if="discount.type === 'ticket'"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="discount.siteShowingIds"
              class="col"
              :label="'Showings To ' + (discount.excludeShowings ? 'Exclude' : 'Include')"
              stack-label
              :options="$_.unionBy(discount.siteShowings.data, showings.data, 'id')"
              map-options
              emit-value
              option-value="id"
              use-input
              input-style="search"
              input-debounce="300"
              outlined
              multiple
              clearable
              bottom-slots
              @filter="filterShowings"
              @clear="discount.siteShowingIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.movie"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.movie.name + ' - ' + scope.opt.siteTime"
                  style="max-width: 280px;"
                  @remove="scope.removeAtIndex(scope.index)"
                >
                  <q-tooltip>{{ scope.opt.movie.name + ' - ' + scope.opt.siteTime }}</q-tooltip>
                </q-chip>
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.movie.name }} - {{ scope.opt.siteTime }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeShowings"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'activity-ticket'"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="discount.siteActivitySessionIds"
              class="col"
              :label="'Activity Sessions To ' + (discount.excludeActivitySessions ? 'Exclude' : 'Include')"
              stack-label
              :options="$_.unionBy(discount.siteActivitySessions.data, activitySessions.data, 'id')"
              map-options
              emit-value
              option-value="id"
              input-style="search"
              use-input
              input-debounce="300"
              outlined
              multiple
              clearable
              bottom-slots
              @filter="filterActivitySessions"
              @clear="discount.siteActivitySessionIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.activity"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.activity.name + ' - ' + scope.opt.siteStartTime"
                  style="max-width: 280px;"
                  @remove="scope.removeAtIndex(scope.index)"
                >
                  <q-tooltip>{{ scope.opt.activity.name + ' - ' + scope.opt.siteStartTime }}</q-tooltip>
                </q-chip>
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.activity.name }} - {{ scope.opt.siteStartTime }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeActivitySessions"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'item'"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="discount.itemClassIds"
              class="col"
              :label="(discount.excludeItemClasses ? 'Exclude' : 'Include') + ' Item Classes'"
              stack-label
              :options="$_.unionBy(discount.itemClasses.data, itemClasses.data, 'id')"
              map-options
              emit-value
              option-value="id"
              multiple
              clearable
              bottom-slots
              @clear="discount.itemClassIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.name }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeItemClasses"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'item'"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="discount.siteItemIds"
              class="col"
              :label="(discount.excludeItems ? 'Exclude' : 'Include') + ' Items'"
              stack-label
              :options="$_.unionBy(discount.siteItems.data, filteredItems, 'id')"
              map-options
              emit-value
              option-value="id"
              use-input
              input-style="search"
              input-debounce="100"
              multiple
              clearable
              bottom-slots
              @filter="filterItems"
              @clear="discount.siteItemIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.name }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeItems"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'item'"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="discount.siteItemVariationIds"
              class="col"
              :label="(discount.excludeItemVariations ? 'Exclude' : 'Include') + ' Item Variations'"
              stack-label
              :options="$_.unionBy(discount.siteItemVariations.data, filteredItemVariations, 'id')"
              map-options
              emit-value
              option-label="nameWithItem"
              option-value="id"
              use-input
              input-style="search"
              input-debounce="100"
              multiple
              clearable
              bottom-slots
              @filter="filterItemVariations"
              @clear="discount.siteItemVariationIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.nameWithItem"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.nameWithItem }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeItemVariations"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'voucher-purchase'"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="discount.voucherTypeIds"
              class="col"
              :label="(discount.excludeVoucherTypes ? 'Exclude' : 'Include') + ' Voucher Types'"
              stack-label
              :options="$_.unionBy(discount.voucherTypes.data, $_.filter(voucherTypes.data, voucherType => voucherType.forSale), 'id')"
              map-options
              emit-value
              option-value="id"
              multiple
              clearable
              bottom-slots
              @clear="discount.voucherTypeIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.name }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeVoucherTypes"
              label="Exclude"
            />
          </div>
          <div
            v-if="discount.type === 'membership-interval'"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="discount.membershipTypeIds"
              class="col"
              :label="(discount.excludeMembershipTypes ? 'Exclude' : 'Include') + ' Membership Types'"
              stack-label
              :options="$_.unionBy(discount.membershipTypes.data, membershipTypes.data, 'id')"
              map-options
              emit-value
              option-value="id"
              multiple
              clearable
              bottom-slots
              @clear="discount.membershipTypeIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  :removable="!discount.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.name }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeMembershipTypes"
              label="Exclude"
            />
          </div>
          <div class="row q-col-gutter-x-sm items-center">
            <model-form-select
              field="conditions"
              class="col-12 q-mt-md"
              label="Conditions"
              :options="$mh.asOptions(DISCOUNT_CONDITIONS.ALL)"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="conditions => $mh.objectToArray(conditions)"
              :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: DISCOUNT_CONDITIONS.ALL })"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="row items-center justify-between q-gutter-x-sm">
                      <span>{{ scope.opt.label }}</span>
                      <q-icon
                        name="info"
                        size="sm"
                      >
                        <q-tooltip>
                          {{ DISCOUNT_CONDITIONS_TOOLTIPS[scope.opt.value] }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip>
                Set conditions to be met for members to redeem this discount. If multiple conditions are selected, all must be met for redemption.
              </q-tooltip>
            </model-form-select>
            <!-- Condition Requirements -->
            <dates-condition-form
              v-if="discount.conditions[DISCOUNT_CONDITIONS.DATES]"
              :conditional="discount"
            />

            <date-range-condition-form
              v-if="discount.conditions[DISCOUNT_CONDITIONS.DATE_RANGE]"
              :conditional="discount"
            />

            <day-of-week-condition-form
              v-if="discount.conditions[DISCOUNT_CONDITIONS.DAY_OF_WEEK]"
              :conditional="discount"
            />

            <sites-condition-form
              v-if="discount.conditions[DISCOUNT_CONDITIONS.SITES]"
              :conditional="discount"
            />

            <time-of-day-condition-form
              v-if="discount.conditions[DISCOUNT_CONDITIONS.TIME_OF_DAY]"
              :conditional="discount"
            />
          </div>
          <limit-by-quantity-form
            :conditional="discount"
          />
        </div>
      </q-tab-panel>
      <q-tab-panel
        v-if="discount.type === 'ticket' && $isPermitted($permissions.OperationsReporting.View)"
        name="reporting"
        class="q-pt-none"
      >
        <div class="column">
          <div class="row q-col-gutter-sm items-start">
            <model-form-toggle
              field="hasCustomReportingName"
              class="col-6"
              label="Has Reporting Name"
            >
              <q-tooltip>
                The reporting name used to group ticket types for internal reporting
              </q-tooltip>
            </model-form-toggle>
            <model-form-input
              field="customReportingName"
              class="col-6"
              label="Custom Reporting Name"
              :disable="!discount.hasCustomReportingName"
            >
              <q-tooltip>
                When used to discount a ticket type, this will override the name of the ticket type in reporting, if prioritised in the ticket type reporting priorities
              </q-tooltip>
            </model-form-input>
          </div>
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>
import { DISCOUNT_AMOUNT_TYPES, DISCOUNT_CONDITIONS } from 'src/models/discount/Discount'
import DateRangeConditionForm from 'src/modules/admin/components/condition-requirement-forms/DateRangeConditionForm'
import DatesConditionForm from 'src/modules/admin/components/condition-requirement-forms/DatesConditionForm'
import DayOfWeekConditionForm from 'src/modules/admin/components/condition-requirement-forms/DayOfWeekConditionForm'
import LimitByQuantityForm from 'src/modules/admin/components/condition-requirement-forms/LimitByQuantityForm'
import SitesConditionForm from 'src/modules/admin/components/condition-requirement-forms/SitesConditionForm'
import TimeOfDayConditionForm from 'src/modules/admin/components/condition-requirement-forms/TimeOfDayConditionForm'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import DiscountPreview from 'src/modules/admin/discount/DiscountPreview'

const DISCOUNT_CONDITIONS_TOOLTIPS = {
  'day-of-week': 'Discounts will be redeemable for showings on the given day of the week.',
  'time-of-day': 'Discounts will be redeemable for showings during a given time range.',
  'date-range': 'Discounts will be redeemable for showings during a given date range.',
  dates: 'Discounts will be redeemable for showings on the given dates.',
  sites: 'Discounts will be redeemable on orders for the given sites.'
}

export default {
  name: 'DiscountForm',
  components: {
    DateRangeConditionForm,
    DatesConditionForm,
    DayOfWeekConditionForm,
    ModelForm,
    SitesConditionForm,
    TimeOfDayConditionForm,
    DiscountPreview,
    LimitByQuantityForm
  },
  configuration: ['currentCircuit', 'currentSite', 'items', 'itemClasses', 'itemVariations', 'titleClasses', 'ticketTypes', 'voucherTypes', 'membershipTypes'],
  data () {
    return {
      tab: 'details',
      showingSearchString: '',
      movieSearchString: '',
      activitySearchString: '',
      activitySessionSearchString: '',
      itemSearchString: '',
      itemVariationSearchString: '',
      showStartTimeClock: false,
      showEndTimeClock: false,
      showExcludeDatesCalendar: false,
      excludeDateDate: null
    }
  },
  models: {
    discount: {
      items: {
        fields: ['id', 'name', 'itemClassId']
      },
      siteItems: {
        fields: ['id', 'name', 'itemClassId']
      },
      siteItemIds: true,
      itemVariations: {
        fields: ['id', 'name', 'itemId'],
        item: {
          fields: ['id', 'name']
        }
      },
      siteItemVariationIds: true,
      sites: {
        fields: ['id', 'name']
      },
      siteTicketTypes: {
        fields: ['id', 'name']
      },
      titleClasses: {
        fields: ['id', 'name']
      },
      siteMovies: {
        fields: ['id', 'name']
      },
      siteMovieIds: 'default',
      siteShowings: {
        fields: ['id', 'time'],
        movie: {
          fields: ['id', 'name'],
          titleClass: {
            fields: ['id', 'name']
          }
        },
        screen: {
          fields: ['id', 'name']
        }
      },
      siteShowingIds: true,
      siteActivities: {
        fields: ['id', 'name']
      },
      siteActivityIds: true,
      siteActivitySessions: {
        activity: 'default'
      },
      siteActivitySessionIds: true,
      itemClasses: {
        fields: ['id', 'name']
      },
      itemClassIds: true,
      voucherTypes: {
        fields: ['id', 'name']
      },
      voucherTypeIds: true,
      membershipTypes: {
        fields: ['id', 'name']
      },
      membershipTypeIds: true,
      titleClassIds: true
    },
    movies: {
      fields: ['id', 'name'],
      variables () {
        return {
          searchString: this.movieSearchString,
          orderBy: 'createdAt',
          descending: true,
          limit: 5
        }
      }
    },
    showings: {
      fields: ['id', 'time'],
      movie: {
        fields: ['id', 'name']
      },
      variables () {
        return {
          searchString: this.showingSearchString,
          orderBy: 'time',
          period: 'future',
          limit: 5
        }
      }
    },
    activities: {
      fields: ['id', 'name'],
      variables () {
        return {
          searchString: this.activitySearchString,
          orderBy: 'createdAt',
          descending: true,
          limit: 5
        }
      }
    },
    activitySessions: {
      activity: 'default',
      variables () {
        return {
          searchString: this.activitySessionSearchString,
          orderBy: 'startTime',
          period: 'future',
          limit: 5
        }
      }
    }
  },
  computed: {
    activeItems () {
      return _.filter(this.items.data, { isUnarchived: true })
    },
    filteredItems () {
      return _.filter(this.activeItems, item => {
        if (!_.isEmpty(this.discount.itemClassIds) && this.discount.excludeItemClasses === _.includes(this.discount.itemClassIds, item.itemClassId)) {
          return false
        }

        return _.includes(item.name.toLowerCase(), this.itemSearchString)
      })
    },
    activeItemVariations () {
      return _.filter(this.itemVariations.data, { isUnarchived: true })
    },
    filteredItemVariations () {
      return _.filter(this.activeItemVariations, itemVariation => {
        if (!_.isEmpty(this.discount.siteItemIds) && this.discount.excludeItems === _.includes(this.discount.siteItemIds, itemVariation.itemId)) {
          return false
        }

        return _.includes(itemVariation.nameWithItem.toLowerCase(), this.itemVariationSearchString)
      })
    },
    discountAmountLabel () {
      const prefix = this.discount.amountType === DISCOUNT_AMOUNT_TYPES.FIXED_AMOUNT ? '' : 'Target '
      return `${prefix}${this.discount.type === 'ticket' ? this.currentSite.ticketAmountLabel : this.currentSite.itemAmountLabel}`
    }
  },
  watch: {
    'discount.limitByQuantityFilter' (val) {
      if (val) {
        this.discount.limitByQuantityScope = null
      }
    }
  },
  created () {
    _.extend(this, { DISCOUNT_AMOUNT_TYPES, DISCOUNT_CONDITIONS, DISCOUNT_CONDITIONS_TOOLTIPS })
  },
  methods: {
    filterShowings (val, update, abort) {
      update(() => {
        this.showingSearchString = val
      })
    },
    filterMovies (val, update, abort) {
      update(() => {
        this.movieSearchString = val
      })
    },
    filterActivities (val, update, abort) {
      update(() => {
        this.activitySearchString = val
      })
    },
    filterActivitySessions (val, update, abort) {
      update(() => {
        this.activitySessionSearchString = val
      })
    },
    filterItems (val, update, abort) {
      update(() => {
        this.itemSearchString = val.toLowerCase()
      })
    },
    filterItemVariations (val, update, abort) {
      update(() => {
        this.itemVariationSearchString = val.toLowerCase()
      })
    },
    onTabChange (tab) {
      this.$refs.modelForm.validateForm(
        () => { this.tab = tab },
        () => { this.$refs.modelForm.showErrorBanner() }
      )
    }
  }
}

</script>
